// src/pages/auth/EmailVerificationPage.tsx

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  CssBaseline
} from "@mui/material";
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import { authService } from "../../services/authService";
import { buildApiUrl, API_ENDPOINTS } from "../../config/api.config";

// Importando constantes de cores do tema centralizado
import { 
  VERDE_MENTA, 
  VERDE_MENTA_ESCURO, 
  FUNDO_ESCURO 
} from "../../themes/themeConstants";

interface EmailVerificationPageProps {
  mode?: 'pending' | 'verify';
}

export const EmailVerificationPage: React.FC<EmailVerificationPageProps> = ({ mode = 'pending' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  // Dados vindos do registro
  const email = location.state?.email || '';
  const userId = location.state?.userId || '';
  const message = location.state?.message || '';

  // Verificar token automaticamente se estivermos no modo 'verify'
  useEffect(() => {
    if (mode === 'verify' && token) {
      verifyEmailToken(token);
    }
  }, [mode, token]);

  const verifyEmailToken = async (verificationToken: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.auth.verifyEmail(verificationToken)), {
        method: 'GET',
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        
        // Aguardar 2 segundos e redirecionar para login
        setTimeout(() => {
          navigate('/auth/login', {
            replace: true,
            state: {
              message: 'Email verificado com sucesso! Faça login para continuar.',
              email: result.user?.email
            }
          });
        }, 2000);
      } else {
        setError(result.error?.message || 'Erro ao verificar email');
      }
    } catch (err: any) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!userId) {
      setError('Dados de usuário não encontrados');
      return;
    }

    setResendLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.auth.resendVerification), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ userId, email })
      });

      const result = await response.json();

      if (result.success) {
        setError(null);
        // Mostrar mensagem de sucesso temporariamente
        setTimeout(() => {
          setError(null);
        }, 5000);
      } else {
        setError(result.error?.message || 'Erro ao reenviar email');
      }
    } catch (err: any) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth/login', { replace: true });
  };

  if (mode === 'verify') {
    // Página de verificação (quando usuário clica no link do email)
    return (
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: FUNDO_ESCURO,
        }}
      >
        <CssBaseline />
        <Container maxWidth="sm">
          <Paper
            elevation={6}
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: 3,
            }}
          >
            {loading && (
              <>
                <CircularProgress size={60} sx={{ color: VERDE_MENTA, mb: 3 }} />
                <Typography variant="h5" gutterBottom>
                  Verificando seu email...
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  Aguarde enquanto confirmamos sua conta.
                </Typography>
              </>
            )}

            {success && (
              <>
                <Avatar sx={{ bgcolor: VERDE_MENTA, mb: 3, width: 64, height: 64 }}>
                  <CheckCircleIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" gutterBottom color={VERDE_MENTA}>
                  ✅ Email Verificado!
                </Typography>
                <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
                  Sua conta foi ativada com sucesso!
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Redirecionando para o login...
                </Typography>
              </>
            )}

            {error && (
              <>
                <Avatar sx={{ bgcolor: 'error.main', mb: 3, width: 64, height: 64 }}>
                  <ErrorIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom color="error">
                  Erro na Verificação
                </Typography>
                <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                  {error}
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleBackToLogin}
                  sx={{
                    backgroundColor: VERDE_MENTA,
                    '&:hover': { backgroundColor: VERDE_MENTA_ESCURO },
                  }}
                >
                  Voltar ao Login
                </Button>
              </>
            )}
          </Paper>
        </Container>
      </Box>
    );
  }

  // Página de aguardo (após registro)
  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: FUNDO_ESCURO,
      }}
    >
      <CssBaseline />
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 3,
          }}
        >
          <Avatar sx={{ bgcolor: VERDE_MENTA, mb: 3, width: 64, height: 64 }}>
            <EmailIcon sx={{ fontSize: 40 }} />
          </Avatar>

          <Typography variant="h4" gutterBottom textAlign="center">
            📧 Verifique seu Email
          </Typography>

          <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
            {message || 'Um email de verificação foi enviado para sua caixa de entrada.'}
          </Typography>

          {email && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Email enviado para: <strong>{email}</strong>
            </Typography>
          )}

          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              • Verifique sua caixa de entrada
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Não se esqueça de verificar a pasta de spam
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Clique no link do email para ativar sua conta
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', width: '100%' }}>
            <Button
              variant="outlined"
              onClick={handleResendEmail}
              disabled={resendLoading || !userId}
              startIcon={resendLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              sx={{
                borderColor: VERDE_MENTA,
                color: VERDE_MENTA,
                '&:hover': {
                  borderColor: VERDE_MENTA_ESCURO,
                  backgroundColor: 'rgba(0, 210, 165, 0.1)',
                },
              }}
            >
              {resendLoading ? 'Reenviando...' : 'Reenviar Email'}
            </Button>

            <Button
              variant="text"
              onClick={handleBackToLogin}
              sx={{ color: 'text.secondary' }}
            >
              Voltar ao Login
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EmailVerificationPage;